import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { X, Download, UploadCloud, FileSpreadsheet, CheckCircle2, AlertTriangle } from 'lucide-react';
import { downloadMocPatientSample, bulkUploadMocPatients } from '../../redux/mocSlice';

// SUPER ADMIN BULK-CREATES PEOPLE (owner, 2026-09-02) — the ONLY bulk-creation
// path on the platform (the tenant app's was deleted the same day). Every row
// makes ONE person row with a Mitra One Care id and no hospital; a hospital
// gives them a patient number when they visit. Same rule as the single Add
// form (owner, 2 Oct 2026): AT LEAST ONE contact per row — a row with only an
// e-mail or only a mobile number is a complete row — nothing marked verified,
// and each person gets their login and a temporary password, verifying their
// contacts at their first sign-in. The server judges every row (one bad row
// never aborts the batch), so this modal only says the rule and reports.
//
// THE TEMPLATE'S COLUMNS ARE THE API'S: Name, Surname, Email, Phone, Gender,
// DOB, Address, City, Zip code, Country, Blood group, Alt phone, Occupation
// (MOC_PATIENT_EXCEL_HEADERS in mocPatientAdminController, matched by header
// name — and by a handful of aliases — so column order does not matter). Every
// other column is ignored by the importer, so the copy below does not invite
// one. Country is the country ID from Masters because the server validates it
// against that master (400 INVALID_COUNTRY); blood group is one of A+ … O-.
//
// UX mirrors the old tenant modal (download sample → pick file → upload →
// per-row results), minus the separate format-check round trip: the upload
// itself answers per row, and one bad row never aborts the batch.
export default function MocBulkUploadModal({ isOpen, onClose, onComplete }) {
  const { t } = useTranslation(['patientpage']);
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const reset = () => {
    setFile(null);
    setFileError('');
    setUploading(false);
    setResult(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleDownloadSample = async () => {
    setDownloading(true);
    try {
      await dispatch(downloadMocPatientSample()).unwrap();
    } catch (err) {
      toast.warning(err?.error || t('moc.bulk_download_failed', 'Failed to download sample'), { autoClose: 2500 });
    } finally {
      setDownloading(false);
    }
  };

  const pickFile = (selected) => {
    setResult(null);
    setFileError('');
    // .xlsx ONLY — the endpoint parses the xlsx zip format alone; a legacy
    // .xls or .ods would bounce server-side, so don't invite one here.
    const ext = (selected.name.split('.').pop() || '').toLowerCase();
    if (ext !== 'xlsx') {
      setFile(null);
      setFileError(t('moc.bulk_invalid_type', 'Only Excel Workbook files (.xlsx) are allowed'));
      return;
    }
    if (selected.size > 5 * 1024 * 1024) {
      setFile(null);
      setFileError(t('moc.bulk_too_large', 'File is too large (max 5MB)'));
      return;
    }
    setFile(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await dispatch(bulkUploadMocPatients(file)).unwrap();
      setResult(res);
      toast.success(
        t('moc.bulk_done', '{{n}} patient(s) created', { n: res?.summary?.created || 0 }),
        { autoClose: 2500 }
      );
      if (onComplete) onComplete();
    } catch (err) {
      toast.warning(err?.error || t('moc.bulk_failed', 'Bulk upload failed'), { autoClose: 2500 });
    } finally {
      setUploading(false);
    }
  };

  const summary = result?.summary;
  const failedRows = (result?.details || []).filter((d) => d.status === 'failed');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="moc-bulk-upload-modal">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#3AA8A8]" />
            <h2 className="text-lg font-bold text-gray-800">{t('moc.bulk_title', 'Bulk upload patients')}</h2>
          </div>
          <button data-testid="moc-bulk-upload-close" onClick={handleClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm text-gray-600 mb-3">
              {t('moc.bulk_step1',
                'Download the template and fill one row per patient — Name plus at least one of Email / Phone; a row with only an e-mail or only a mobile number is fine. Everything else is optional: Surname, Gender, DOB (YYYY-MM-DD), Address, City, Zip code, Country (the country ID from Masters), Blood group (A+ … O-), Alt phone and Occupation. Each row creates the person’s Mitra One Care profile; a hospital gives them a patient number when they visit. Each person receives their login and a temporary password, and verifies their contact details at their first sign-in.')}
            </p>
            <button
              data-testid="moc-bulk-download-sample"
              onClick={handleDownloadSample}
              disabled={downloading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-[#4ABDBD] text-[#3AA8A8] font-semibold hover:bg-[#ecfdfd] transition text-sm disabled:opacity-60 cursor-pointer"
            >
              <Download className="w-4 h-4" />
              {downloading ? t('moc.bulk_downloading', 'Downloading…') : t('moc.bulk_download', 'Download Sample Excel')}
            </button>
          </div>

          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) pickFile(f); }}
            className="rounded-xl border-2 border-dashed border-gray-300 p-6 text-center hover:border-[#4ABDBD] transition"
          >
            <UploadCloud className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <label className="cursor-pointer">
              <span className="text-[#3AA8A8] font-semibold underline">{t('moc.bulk_choose', 'Choose a file')}</span>
              <span className="text-gray-500 text-sm"> {t('moc.bulk_or_drag', 'or drag and drop (.xlsx, max 5MB)')}</span>
              <input
                data-testid="moc-bulk-file-input"
                type="file"
                accept=".xlsx"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) pickFile(f); }}
                className="hidden"
              />
            </label>
            {file && (
              <div className="mt-3 text-sm text-gray-700 flex items-center justify-center gap-2">
                <FileSpreadsheet className="w-4 h-4 text-[#3AA8A8]" />
                {file.name}
              </div>
            )}
            {fileError && (
              <div className="mt-2 text-xs text-red-600 flex items-center justify-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {fileError}
              </div>
            )}
          </div>

          {summary && (
            <div className="rounded-xl border border-gray-200 p-4" data-testid="moc-bulk-result">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="rounded-lg bg-gray-50 p-3">
                  <div className="text-2xl font-bold text-gray-700">{summary.total}</div>
                  <div className="text-xs text-gray-500">{t('moc.bulk_rows', 'Rows')}</div>
                </div>
                <div className="rounded-lg bg-green-50 p-3">
                  <div className="text-2xl font-bold text-green-600">{summary.created}</div>
                  <div className="text-xs text-gray-500">{t('moc.bulk_created', 'Created')}</div>
                </div>
                <div className="rounded-lg bg-red-50 p-3">
                  <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
                  <div className="text-xs text-gray-500">{t('moc.bulk_errors', 'Failed')}</div>
                </div>
              </div>

              {failedRows.length > 0 && (
                <div className="mt-4 max-h-40 overflow-y-auto text-xs space-y-1">
                  {failedRows.map((d, i) => (
                    <div key={`err-${i}`} className="flex items-center gap-2 text-red-700">
                      <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{t('moc.bulk_row', 'Row')} {d.row}: {d.email || d.phone || d.name || '—'} — {d.error}</span>
                    </div>
                  ))}
                </div>
              )}
              {summary.created > 0 && (
                <div className="mt-3 text-xs text-green-700 flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  {t('moc.bulk_created_note', 'New patients appear in the list with “Joined via: By super admin”.')}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-5 border-t border-gray-100">
          <button
            data-testid="moc-bulk-cancel"
            onClick={handleClose}
            className="px-4 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-semibold cursor-pointer"
          >
            {result ? t('moc.bulk_close', 'Close') : t('moc.bulk_cancel', 'Cancel')}
          </button>
          {!result && (
            <button
              data-testid="moc-bulk-upload-button"
              onClick={handleUpload}
              disabled={!file || uploading}
              className={`px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] ${(!file || uploading) ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}`}
            >
              {uploading ? t('moc.bulk_uploading', 'Uploading…') : t('moc.bulk_upload_btn', 'Upload')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
